import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { decideBrainApproval, fetchPendingApprovals } from '../api/brain'
import {
  fetchCockpitNarrative,
  fetchCockpitStatus,
  fetchOpenWorkOrders,
  fetchWorkqueue,
  generateCockpitNarrative,
} from '../api/cockpit'

export function Dashboard() {
  const queryClient = useQueryClient()
  const [narrativeError, setNarrativeError] = useState<string | null>(null)
  const [narrativeGenerating, setNarrativeGenerating] = useState(false)
  const [approvalActionError, setApprovalActionError] = useState<string | null>(null)
  const [approvalActionId, setApprovalActionId] = useState<string | null>(null)

  const statusQuery = useQuery({
    queryKey: ['cockpit', 'status'],
    queryFn: fetchCockpitStatus,
    refetchInterval: 60_000,
  })
  const narrativeQuery = useQuery({
    queryKey: ['cockpit', 'narrative', 'latest'],
    queryFn: () => fetchCockpitNarrative('latest'),
    refetchInterval: 60_000,
  })
  const workqueueQuery = useQuery({
    queryKey: ['cockpit', 'workqueue'],
    queryFn: fetchWorkqueue,
    staleTime: 60_000,
  })
  const workOrdersQuery = useQuery({
    queryKey: ['cockpit', 'open-work-orders'],
    queryFn: fetchOpenWorkOrders,
    refetchInterval: 60_000,
  })
  const pendingApprovalsQuery = useQuery({
    queryKey: ['brain', 'approvals', 'pending-dashboard'],
    queryFn: fetchPendingApprovals,
    refetchInterval: 30_000,
  })

  const handleGenerateNarrative = useCallback(async () => {
    setNarrativeGenerating(true)
    setNarrativeError(null)
    try {
      const res = await generateCockpitNarrative()
      if (res.status !== 'ok') {
        setNarrativeError(res.error || 'Could not generate narrative.')
        return
      }
      await queryClient.invalidateQueries({ queryKey: ['cockpit', 'narrative'] })
    } catch (err) {
      setNarrativeError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setNarrativeGenerating(false)
    }
  }, [queryClient])

  const handleApprovalAction = useCallback(async (approvalDocId: string, action: 'approve' | 'reject') => {
    setApprovalActionError(null)
    setApprovalActionId(approvalDocId)
    try {
      await decideBrainApproval(approvalDocId, action === 'approve' ? 'approved' : 'rejected')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['brain', 'approvals'] }),
        queryClient.invalidateQueries({ queryKey: ['brain', 'work-orders'] }),
        queryClient.invalidateQueries({ queryKey: ['cockpit', 'status'] }),
        queryClient.invalidateQueries({ queryKey: ['cockpit', 'open-work-orders'] }),
      ])
    } catch (err) {
      setApprovalActionError(err instanceof Error ? err.message : 'Approval action failed')
    } finally {
      setApprovalActionId(null)
    }
  }, [queryClient])

  if (statusQuery.isLoading || narrativeQuery.isLoading || workqueueQuery.isLoading || workOrdersQuery.isLoading || pendingApprovalsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading cockpit...</div>
      </div>
    )
  }

  const errorMessage = [statusQuery.error, narrativeQuery.error, workqueueQuery.error, workOrdersQuery.error, pendingApprovalsQuery.error]
    .find(Boolean)

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-red">
          {errorMessage instanceof Error ? errorMessage.message : 'Failed to load cockpit'}
        </div>
      </div>
    )
  }

  const status = statusQuery.data!
  const narrative = narrativeQuery.data!
  const objectives = workqueueQuery.data?.objectives || []
  const openWorkOrders = workOrdersQuery.data || []
  const pendingApprovals = pendingApprovalsQuery.data || []
  const firstParagraph = narrative.content.split(/\n{2,}/).map((part) => part.trim()).find((part) => part && !part.startsWith('#') && !part.startsWith('>')) || 'No narrative for today yet.'

  const todayUtc = new Date().toISOString().slice(0, 10)
  const narrativeDay = narrative.date.slice(0, 10)
  const needsNarrative = narrativeDay !== todayUtc

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Dashboard</h1>
        <p className="text-swiss-gray-500 mt-2 text-[14px]">
          Morning briefing, active objectives, and open coordination loops.
        </p>
      </div>

      {status.degraded && (
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-950">
          Cockpit KPIs are in degraded mode — brain server data was unavailable for one or more feeds.
        </div>
      )}

      {pendingApprovals.length > 0 && (
        <section className="border border-swiss-black bg-swiss-off-white px-6 py-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-widest text-swiss-gray-500">Pending approvals</p>
              <h2 className="mt-1 text-[18px] font-semibold tracking-tight">
                {pendingApprovals.length} work order{pendingApprovals.length === 1 ? '' : 's'} waiting on you
              </h2>
            </div>
            <Link to="/work-orders" className="text-[13px] font-medium text-swiss-black underline underline-offset-2">
              Open queue →
            </Link>
          </div>
          {approvalActionError && (
            <p className="mt-3 text-[13px] text-swiss-red">{approvalActionError}</p>
          )}
          <div className="mt-5 space-y-3">
            {pendingApprovals.slice(0, 3).map((wo) => {
              const actionId = wo._doc_id
              const actionPending = approvalActionId === actionId
              return (
                <div key={actionId} className="border border-swiss-gray-200 bg-swiss-white px-4 py-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-swiss-gray-500">
                        <span>{wo.tier}</span>
                        <span>{wo.brainId || 'unknown brain'}</span>
                        <span>{wo.tool}</span>
                      </div>
                      <h3 className="mt-2 text-[15px] font-semibold tracking-tight text-swiss-black">
                        Approval required for {wo.tool}
                      </h3>
                      <p className="mt-2 max-w-[80ch] whitespace-pre-wrap text-[13px] leading-6 text-swiss-gray-600">
                        {wo.reason || 'No approval reason provided.'}
                      </p>
                      <p className="mt-2 max-w-[80ch] whitespace-pre-wrap break-all text-[12px] leading-5 text-swiss-gray-500">
                        {typeof wo.input?.command === 'string'
                          ? wo.input.command
                          : JSON.stringify(wo.input)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => void handleApprovalAction(actionId, 'approve')}
                        disabled={actionPending}
                        className="px-4 py-2 bg-swiss-black text-swiss-white text-[13px] font-medium disabled:opacity-50"
                      >
                        {actionPending ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleApprovalAction(actionId, 'reject')}
                        disabled={actionPending}
                        className="px-4 py-2 border border-swiss-gray-200 text-[13px] font-medium text-swiss-black disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatusTile
          label="Unread incoming"
          value={String(status.unread_incoming)}
          detail="Inbox messages to you (unread)"
        />
        <StatusTile
          label="Awaiting your action"
          value={String(status.open_awaiting_action)}
          detail="Work orders pending your approval"
        />
        <StatusTile
          label="Dispatched from you"
          value={String(status.open_dispatched)}
          detail="Approved or in progress (from you)"
        />
        <StatusTile
          label="Stale (>7d)"
          value={String(status.stale_over_7d)}
          detail="Open work orders with no update in 7+ days"
        />
      </div>

      <section className="border border-swiss-gray-200 bg-swiss-off-white px-6 py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-widest text-swiss-gray-500">Latest Narrative</p>
            <h2 className="mt-2 text-[22px] font-semibold tracking-tight">{narrative.title}</h2>
            <p className="mt-1 text-[12px] font-swiss-mono text-swiss-gray-400">{narrative.date}</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <Link to="/narratives" className="text-[13px] font-medium text-swiss-black underline underline-offset-2">
              Read full narrative →
            </Link>
            {needsNarrative && (
              <button
                type="button"
                onClick={() => void handleGenerateNarrative()}
                disabled={narrativeGenerating}
                className="text-[13px] font-medium border border-swiss-black px-3 py-1.5 text-swiss-black hover:bg-swiss-off-white disabled:opacity-50"
              >
                {narrativeGenerating ? 'Generating…' : "Generate today's narrative"}
              </button>
            )}
          </div>
        </div>
        {narrativeError && (
          <p className="mt-3 text-[13px] text-swiss-red">{narrativeError}</p>
        )}
        <p className="mt-5 max-w-[72ch] whitespace-pre-wrap text-[14px] leading-6 text-swiss-black">{firstParagraph}</p>
      </section>

      <section className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[16px] font-semibold tracking-tight">Active Objectives</h2>
            <span className="text-[11px] uppercase tracking-widest text-swiss-gray-400">{objectives.length} tracked</span>
          </div>
          <div className="border border-swiss-gray-200">
            {objectives.length === 0 ? (
              <div className="px-5 py-6 text-[13px] text-swiss-gray-500">No active objectives found.</div>
            ) : (
              objectives.map((objective, index) => (
                <div key={`${objective.title}-${index}`} className="border-b border-swiss-gray-100 px-5 py-4 last:border-b-0">
                  <h3 className="text-[14px] font-semibold text-swiss-black">{objective.title}</h3>
                  {objective.details && <p className="mt-1 text-[12px] text-swiss-gray-500">{objective.details}</p>}
                  <p className="mt-3 text-[13px] leading-6 text-swiss-black">
                    <span className="font-medium">Next:</span> {objective.next}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[16px] font-semibold tracking-tight">Open Work Orders</h2>
            <Link to="/work-orders" className="text-[13px] font-medium text-swiss-black underline underline-offset-2">
              View all →
            </Link>
          </div>
          <div className="border border-swiss-gray-200">
            {openWorkOrders.length === 0 ? (
              <div className="px-5 py-6 text-[13px] text-swiss-gray-500">No open work orders.</div>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_2fr_120px_90px] border-b border-swiss-gray-200 bg-swiss-off-white px-5 py-3 text-[11px] uppercase tracking-widest text-swiss-gray-500">
                  <div>To</div>
                  <div>Subject</div>
                  <div>Sent</div>
                  <div>Age</div>
                </div>
                {openWorkOrders.map((wo) => (
                  <div key={wo.id} className="grid grid-cols-[1fr_2fr_120px_90px] border-b border-swiss-gray-100 px-5 py-4 text-[13px] last:border-b-0">
                    <div className="font-swiss-mono text-swiss-black">{wo.to}</div>
                    <div className="pr-4 text-swiss-black">{wo.subject}</div>
                    <div className="font-swiss-mono text-swiss-gray-500">
                      {wo.sent_at ? new Date(wo.sent_at).toLocaleDateString() : '—'}
                    </div>
                    <div className="font-swiss-mono text-swiss-gray-500">
                      {wo.age_hours === null ? '—' : `${wo.age_hours}h`}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

function StatusTile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="border border-swiss-gray-200 px-5 py-5">
      <p className="text-[11px] uppercase tracking-widest text-swiss-gray-500">{label}</p>
      <div className="mt-3 text-[32px] font-semibold tracking-tight text-swiss-black">{value}</div>
      <p className="mt-2 text-[12px] text-swiss-gray-400">{detail}</p>
    </div>
  )
}
